import * as Toast from '@radix-ui/react-toast';
import React, { createContext, useContext, useState, useCallback } from 'react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (title: string, description?: string, type?: ToastType) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback(
    (title: string, description?: string, type: ToastType = 'info') => {
      const id = Math.random().toString(36).substring(7);
      setToasts((prev) => [...prev, { id, title, description, type }]);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
      }, 5000);
    },
    []
  );

  const success = useCallback(
    (title: string, description?: string) => {
      showToast(title, description, 'success');
    },
    [showToast]
  );

  const error = useCallback(
    (title: string, description?: string) => {
      showToast(title, description, 'error');
    },
    [showToast]
  );

  const info = useCallback(
    (title: string, description?: string) => {
      showToast(title, description, 'info');
    },
    [showToast]
  );

  const warning = useCallback(
    (title: string, description?: string) => {
      showToast(title, description, 'warning');
    },
    [showToast]
  );

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      <Toast.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <Toast.Root
            key={toast.id}
            open={true}
            onOpenChange={(open) => {
              if (!open) {
                setToasts((prev) => prev.filter((t) => t.id !== toast.id));
              }
            }}
            className={`
              bg-white rounded-lg shadow-lg p-4 border-l-4
              ${toast.type === 'success' ? 'border-green-500' : ''}
              ${toast.type === 'error' ? 'border-red-500' : ''}
              ${toast.type === 'warning' ? 'border-yellow-500' : ''}
              ${toast.type === 'info' ? 'border-blue-500' : ''}
              data-[state=open]:animate-in data-[state=closed]:animate-out
              data-[swipe=end]:animate-out data-[state=closed]:fade-out-80
              data-[state=closed]:slide-out-to-right-full
              data-[state=open]:slide-in-from-top-full
              data-[state=open]:sm:slide-in-from-bottom-full
            `}
          >
            <Toast.Title className="font-semibold text-gray-900 mb-1">
              {toast.title}
            </Toast.Title>
            {toast.description && (
              <Toast.Description className="text-sm text-gray-600">
                {toast.description}
              </Toast.Description>
            )}
            <Toast.Close className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">
              ✕
            </Toast.Close>
          </Toast.Root>
        ))}
        <Toast.Viewport className="fixed bottom-0 right-0 z-50 m-0 flex w-[calc(100%-1.5rem)] max-w-96 list-none flex-col gap-2 p-3 outline-none sm:p-6" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
